import { useState, useEffect, useMemo } from 'react'
import { toast } from '@/hooks/use-toast'
import { LeadFilters } from '@/components/leads/LeadFilters'
import { LeadTable } from '@/components/leads/LeadTable'
import { BulkActionsBar } from '@/components/leads/BulkActionsBar'
import { MobileLeadReview } from '@/components/mobile/MobileLeadReview'
import { useMobileResponsive } from '@/hooks/useMobileResponsive'
import { 
  generateMockLeads, 
  filterLeads, 
  defaultFilters,
  type LeadTableRow,
  type FilterState 
} from '@/services/mockLeads'

export function Leads() {
  const [allLeads, setAllLeads] = useState<LeadTableRow[]>([])
  const [filters, setFilters] = useState<FilterState>(defaultFilters)
  const [selectedLeads, setSelectedLeads] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const { isMobile } = useMobileResponsive()
  
  // Generate mock data on component mount
  useEffect(() => {
    const generateData = async () => {
      setLoading(true)
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const mockLeads = generateMockLeads(500) // Generate 500 leads for testing
      setAllLeads(mockLeads)
      setLoading(false)
    }
    
    generateData()
  }, [])
  
  // Filter leads based on current filters
  const filteredLeads = useMemo(() => {
    return filterLeads(allLeads, filters)
  }, [allLeads, filters])
  
  // Lead actions
  const handleApprove = (leadId: string) => {
    setAllLeads(current => 
      current.map(lead => 
        lead.id === leadId 
          ? { ...lead, status: 'approved' as const, reviewedBy: 'Current User', reviewedAt: new Date() }
          : lead
      )
    )
    
    setSelectedLeads(current => current.filter(id => id !== leadId))
    
    toast({
      title: "Lead Approved",
      description: `Lead ${leadId} has been approved.`,
    })
  }
  
  const handleReject = (leadId: string) => {
    setAllLeads(current => 
      current.map(lead => 
        lead.id === leadId 
          ? { ...lead, status: 'rejected' as const, reviewedBy: 'Current User', reviewedAt: new Date() }
          : lead
      )
    )
    
    setSelectedLeads(current => current.filter(id => id !== leadId))
    
    toast({
      title: "Lead Rejected",
      description: `Lead ${leadId} has been rejected.`,
    })
  }
  
  const handleFlag = (leadId: string) => {
    setAllLeads(current => 
      current.map(lead => 
        lead.id === leadId 
          ? { ...lead, notes: 'Flagged for manual review' }
          : lead
      )
    )
    
    toast({
      title: "Lead Flagged",
      description: `Lead ${leadId} has been flagged for review.`,
    })
  }
  
  const handleNote = (leadId: string) => {
    const note = prompt('Add a note for this lead:')
    if (note) {
      setAllLeads(current => 
        current.map(lead => 
          lead.id === leadId 
            ? { ...lead, notes: note }
            : lead
        )
      )
      
      toast({
        title: "Note Added",
        description: `Note added to lead ${leadId}.`,
      })
    }
  }

  const handleLoadMore = () => {
    if (loadingMore) return
    
    setLoadingMore(true)
    setTimeout(() => {
      const newLeads = generateMockLeads(20)
      setAllLeads(prev => [...prev, ...newLeads])
      setLoadingMore(false)
      
      // Simulate end of data after loading more than 100 leads
      if (allLeads.length > 100) {
        setHasMore(false)
      }
    }, 1000)
  }

  const handleViewDetails = (leadId: string) => {
    toast({
      title: "View Details",
      description: `Viewing details for lead ${leadId}`,
    })
  }
  
  // Bulk actions
  const handleBulkApprove = () => {
    const count = selectedLeads.length
    
    setAllLeads(current => 
      current.map(lead => 
        selectedLeads.includes(lead.id) 
          ? { ...lead, status: 'approved' as const, reviewedBy: 'Current User', reviewedAt: new Date() }
          : lead
      )
    )
    
    setSelectedLeads([])
    
    toast({
      title: "Bulk Approval Complete",
      description: `${count} leads have been approved.`,
    })
  }
  
  const handleBulkReject = () => {
    const count = selectedLeads.length
    
    setAllLeads(current => 
      current.map(lead => 
        selectedLeads.includes(lead.id) 
          ? { ...lead, status: 'rejected' as const, reviewedBy: 'Current User', reviewedAt: new Date() }
          : lead
      )
    )
    
    setSelectedLeads([])
    
    toast({
      title: "Bulk Rejection Complete",
      description: `${count} leads have been rejected.`,
    })
  }
  
  const handleBulkFlag = () => {
    const count = selectedLeads.length
    
    setAllLeads(current => 
      current.map(lead => 
        selectedLeads.includes(lead.id) 
          ? { ...lead, notes: 'Flagged for manual review' }
          : lead
      )
    )
    
    setSelectedLeads([])
    
    toast({
      title: "Bulk Flag Complete",
      description: `${count} leads have been flagged.`,
    })
  }
  
  const handleExport = () => {
    const dataToExport = selectedLeads.length > 0 
      ? filteredLeads.filter(lead => selectedLeads.includes(lead.id))
      : filteredLeads
    
    const csv = [
      // Headers
      'ID,Timestamp,Name,Email,Phone,Zip,IP,Risk Score,Status,Source,Patterns',
      // Data rows
      ...dataToExport.map(lead => [
        lead.id,
        lead.timestamp.toISOString(),
        `"${lead.firstName} ${lead.lastName}"`,
        lead.email,
        lead.phone,
        lead.zip,
        lead.ip,
        lead.riskScore,
        lead.status,
        lead.source,
        `"${Object.entries(lead.patterns)
          .filter(([_, active]) => active)
          .map(([pattern]) => pattern)
          .join(', ')}"`,
      ].join(','))
    ].join('\n')
    
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
    
    toast({
      title: "Export Complete",
      description: `${dataToExport.length} leads exported to CSV.`,
    })
  }
  
  const handleWhitelist = () => {
    toast({
      title: "Added to Whitelist",
      description: `${selectedLeads.length} leads added to whitelist.`,
    })
    setSelectedLeads([])
  }
  
  const handleBlacklist = () => {
    toast({
      title: "Added to Blacklist",
      description: `${selectedLeads.length} leads added to blacklist.`,
    })
    setSelectedLeads([])
  }
  
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-muted/30 rounded-lg animate-pulse" />
        <div className="h-96 bg-muted/30 rounded-lg animate-pulse" />
      </div>
    )
  }

  // Mobile Lead Review
  if (isMobile) {
    return (
      <MobileLeadReview
        leads={filteredLeads}
        onApprove={handleApprove}
        onReject={handleReject}
        onViewDetails={handleViewDetails}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        loading={loadingMore}
      />
    )
  }
  
  return (
    <div className="space-y-6">
      <LeadFilters
        filters={filters}
        onFiltersChange={setFilters}
        totalLeads={allLeads.length}
        filteredLeads={filteredLeads.length}
        onExport={handleExport}
      />
      
      <LeadTable
        leads={filteredLeads}
        selectedLeads={selectedLeads}
        onSelectionChange={setSelectedLeads}
        onApprove={handleApprove}
        onReject={handleReject}
        onFlag={handleFlag}
        onNote={handleNote}
      />
      
      <BulkActionsBar
        selectedCount={selectedLeads.length}
        onApproveAll={handleBulkApprove}
        onRejectAll={handleBulkReject}
        onExportSelected={handleExport}
        onFlagAll={handleBulkFlag}
        onWhitelist={handleWhitelist}
        onBlacklist={handleBlacklist}
        onClearSelection={() => setSelectedLeads([])}
      />
    </div>
  )
}